// ** React Imports
import { useEffect, useState, useRef } from 'react';

// ** Redux Imports
import { useDispatch, useSelector } from 'react-redux';
import { _updateBalancePreference } from '../../../../redux/actions';
import { setUser } from '../../../../../user/redux/slices/userDataSlice';

// ** Styles Imports
import styles from './styles.module.css';

import { formatNumber } from '../../../../../helpers/formatNumber';

// ** Utils Imports
import { showErrorSnackbar } from '../../../../../snackbar/helpers';
import { handleDeposit } from '../../../../../../utility/deposit-withdraw-helpers/handleDeposit';
import { handleWithdraw } from '../../../../../../utility/deposit-withdraw-helpers/handleWithdraw';

// ** Icons Imports
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { ReactComponent as WalletIcon } from '../../../../../../assets/icons/imported/wallet.svg';

// ** Component Imports
import { useMediaQuery } from '@mui/material';
import Button from '../../../../../../components/Button/Button';
import PortfolioGraph from '../PortfolioGraph';
import PortfolioPieChart from '../PortfolioPieChart';
import {
  setSelectCurrencyModalData,
  setSelectCurrencyModalOpen
} from '../../../../../modals/select-currency/redux/selectCurrencyModalSlice';
import { _getFundsData } from '../../../../../../redux/actions';
import { setBtcBalance, setDeposits, setFunds, setSpotBalanceBTC, setSpotBalanceUSDT, setWithdrawals } from '../../../../../../redux/slices/fundsSlice';

function WalletsOverviewTabHeader() {
  // ** Media Queries
  const isMobileView = useMediaQuery('(max-width:1020px)')

  // ** Hooks
  const dispatch = useDispatch()

  // ** Show/Hide Price
  const totalBalance = useSelector(state => state.fundsData.data)
  const hideBalance = useSelector(state => state.userData.value.hide_balance)
  const theme = useSelector(state => state.theme.value)

  // ** States
  const [hideBalanceLoading, setHideBalanceLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [realtimePortfolioValue, setRealtimePortfolioValue] = useState(null)
  const [portfolioMetrics, setPortfolioMetrics] = useState(null)

  // ** WebSocket Ref
  const wsRef = useRef(null)

  // ** WebSocket Functions
  const connectWebSocket = () => {
    // Don't connect if already connected
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      const wsUrl = window.location.host.includes('localhost')
        ? `ws://${window.location.host}/api/v2/ws/private`
        : `wss://${window.location.host}/api/v2/ws/private`;

      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('Portfolio WebSocket connected');

        // Subscribe to portfolio updates
        const subscribeMessage = {
          event: 'subscribe',
          streams: ['portfolio']
        };

        try {
          wsRef.current.send(JSON.stringify(subscribeMessage));
        } catch (error) {
          console.error('Error sending WebSocket subscription:', error);
        }
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.portfolio && data.portfolio.portfolio_usd) {
            // Update real-time portfolio value
            setRealtimePortfolioValue(data.portfolio.portfolio_usd);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      wsRef.current.onclose = (event) => {
        console.log('Portfolio WebSocket disconnected:', event.code, event.reason);

        // Only attempt to reconnect if it wasn't a manual close
        if (event.code !== 1000) {
          setTimeout(() => {
            if (wsRef.current?.readyState !== WebSocket.OPEN) {
              connectWebSocket();
            }
          }, 5000);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('Portfolio WebSocket error:', error);
      };
    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
    }
  };

  const disconnectWebSocket = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  };

  // ** Handlers
  const handleEarn = () => { }

  const handleMetricsUpdate = (metrics) => {
    setPortfolioMetrics(metrics)
  }

  useEffect(() => {
    setLoading(true)
    _getFundsData(
      data => {
        dispatch(setWithdrawals(data.total_withdraw))
        dispatch(setDeposits(data.total_deposit))
        dispatch(setFunds(data.total_balance))
        dispatch(setBtcBalance(data.total_balance_btc))
        dispatch(setSpotBalanceUSDT(data.total_spot_balance))
        dispatch(setSpotBalanceBTC(data.total_spot_balance_btc))

        // Extract portfolio metrics from the updated API response
        const metrics = {
          profitLoss: parseFloat(data.pl_amount || 0),
          profitLossPercentage: parseFloat(data.pl_percentage || 0)
        }
        setPortfolioMetrics(metrics)

        setLoading(false)
      },
      error => {
        console.log(error)
        setLoading(false)
      }
    )

    // Connect WebSocket for real-time updates
    connectWebSocket()

    // Cleanup WebSocket on unmount
    return () => {
      disconnectWebSocket()
    }
  }, [])

  const handleToggleHidePrice = () => {
    setHideBalanceLoading(true)
    _updateBalancePreference(
      { preference: !hideBalance },
      data => {
        dispatch(setUser(data.data))
        setHideBalanceLoading(false)
      },
      () => {
        setHideBalanceLoading(false)
        showErrorSnackbar({ alertMessage: 'Something went wrong' })
      }
    )
  }

  return (
    <div className={styles.modernWalletContainer}>
      {/* Modern Wallet Header */}
      <div className={styles.walletHeader} style={{
        backgroundColor: 'var(--secondary-color)',
        borderRadius: '16px',
        padding: '32px',
        marginBottom: '0px',
        border: '1px solid rgba(139, 154, 171, 0.1)'
      }}>
        <div className={styles.headerTop}>
          <div className={styles.titleSection}>
            <h1 className={styles.walletTitle}>Overview</h1>
            <p className={styles.walletSubtitle}>
              All your crypto techs, stored in one place. Cool huh?
            </p>
          </div>

          <div className={styles.actionButtons}>
            <Button
              text="Deposit"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                borderRadius: "12px",
                padding: "12px 24px",
                fontSize: "14px",
                fontWeight: "600"
              }}
              onClick={() => {
                dispatch(
                  setSelectCurrencyModalData({
                    title: 'Select coin to deposit',
                    showTabs: false,
                    type: 'coin',
                    searchPlaceholder: 'Search coin name',
                    currenciesToHide: [],
                    disableBackdropClick: true,
                    callback: coin => {
                      handleDeposit(coin.id)
                    }
                  })
                )
                dispatch(setSelectCurrencyModalOpen(true))
              }}
            />
            <Button
              text="Withdraw"
              variant="dark"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                borderRadius: "12px",
                padding: "12px 24px",
                fontSize: "14px",
                fontWeight: "600",
                backgroundColor: "transparent",
                border: `1px solid ${theme === "light" ? "rgba(139, 154, 171, 0.3)" : "rgba(139, 154, 171, 0.3)"}`,
                color: theme === "light" ? "#001e28" : "#ffffff"
              }}
              onClick={handleWithdraw}
            />
          </div>
        </div>

        {/* Overview Grid - Similar to SpotWalletTab */}
        <div className={styles.overviewGrid}>
          {/* Left Column - Balance Display */}
          <div className={styles.balanceColumn}>
            <div className={styles.balanceDisplay}>
              <div className={styles.balanceLabel}>
                <WalletIcon width={20} />
                <span>Account Balance</span>
              </div>

              <div className={styles.balanceAmount}>
                <h2 className={styles.balanceValue}>
                  {loading || !totalBalance || totalBalance.total_funds === null || totalBalance.total_funds === undefined ? (
                    <div className={styles.shimmerEffect} style={{ width: '120px', height: '36px', display: 'inline-block' }}></div>
                  ) : (
                    <>
                      {hideBalance ? '••••••••' : `$${formatNumber(realtimePortfolioValue || totalBalance?.total_spot_balance, 2)}`}
                      <span className={styles.currency}>USD</span>
                    </>
                  )}
                </h2>

                <button
                  onClick={handleToggleHidePrice}
                  className={styles.eyeButton}
                  disabled={hideBalanceLoading}
                >
                  {hideBalance ? <FiEye size={16} /> : <FiEyeOff size={16} />}
                </button>
              </div>

              <p className={styles.btcEquivalent}>
                ≈ {loading || !totalBalance || totalBalance.total_balance_btc === null || totalBalance.total_balance_btc === undefined ? (
                  <div className={styles.shimmerEffect} style={{ width: '80px', height: '14px', display: 'inline-block' }}></div>
                ) : (
                  <>
                    {hideBalance ? '••••••••' : formatNumber(totalBalance?.total_balance_btc, 8)} BTC
                  </>
                )}
                {/* Portfolio Growth Percentage */}
                {!loading && portfolioMetrics && (
                  <span style={{
                    marginLeft: '12px',
                    color: portfolioMetrics.profitLoss >= 0 ? '#10b981' : '#ef4444',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                    {hideBalance ? '••••' : (
                      <>
                        ({portfolioMetrics.profitLoss >= 0 ? '+' : ''}{portfolioMetrics.profitLossPercentage.toFixed(2)}%)
                      </>
                    )}
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Right Column - Stats Grid */}
          <div className={styles.statsColumn}>
            <div className={styles.statsGrid}>
              <div className={styles.statCard} style={{ backgroundColor: '#000000' }}>
                <div className={styles.statLabel} style={{ color: '#8b9aab' }}>
                  Total Deposited
                </div>
                <div className={styles.statValue} style={{ color: 'var(--text-color)' }}>
                  {loading || !totalBalance || totalBalance.total_deposits === null || totalBalance.total_deposits === undefined ? (
                    <div className={styles.shimmerEffect} style={{ width: '90px', height: '18px' }}></div>
                  ) : hideBalance ? (
                    '••••••••'
                  ) : (
                    `$${formatNumber(totalBalance?.total_deposits)}`
                  )}
                </div>
              </div>

              <div className={styles.statCard} style={{ backgroundColor: '#000000' }}>
                <div className={styles.statLabel} style={{ color: '#8b9aab' }}>
                  Total Withdrawn
                </div>
                <div className={styles.statValue} style={{ color: 'var(--text-color)' }}>
                  {loading || !totalBalance || totalBalance.total_withdrawals === null || totalBalance.total_withdrawals === undefined ? (
                    <div className={styles.shimmerEffect} style={{ width: '90px', height: '18px' }}></div>
                  ) : hideBalance ? (
                    '••••••••'
                  ) : (
                    `$${formatNumber(totalBalance?.total_withdrawals)}`
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Portfolio Graph Section */}
      <PortfolioGraph onMetricsUpdate={handleMetricsUpdate} />

      {/* Portfolio Pie Chart Section */}
      <PortfolioPieChart />
    </div>
  )
}

export default WalletsOverviewTabHeader
